/**
 * Tournament Overview Page (TiKe - Tilanne Keskus)
 * 
 * Displays tournament progress with configurable grouping:
 * - Games: Individual game boxes per court
 * - Blocks: Block-level completion stats
 * - Rounds: Round + league aggregated summary
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
  LinearProgress,
  Link,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { adminApi } from '@/api/client';
import type {
  TournamentOverviewResponse,
  OverviewCourt,
  OverviewBlock,
  OverviewGame,
  OverviewRound,
  OverviewGameStatus,
} from '@/types';

import './overview.css';

// League colors matching legacy
const LEAGUE_COLORS: Record<string, string> = {
  yle: '#c5e0b4',  // Green
  pro: '#ffe699',  // Yellow
  frm: '#bdd7ee',  // Blue
};

// Game status colors
const STATUS_COLORS: Record<OverviewGameStatus, string> = {
  scheduled: '#f0f0f0',
  started: '#2196f3',    // Blue
  finished: '#4caf50',   // Green
  late: '#f44336',       // Red
  cancelled: '#000000',  // Black
};

function GameBox({ game }: { game: OverviewGame }) {
  const textColor = game.status === 'scheduled' ? '#000' : '#fff';
  
  return (
    <Tooltip 
      title={`${game.team1_name || 'TBA'} vs ${game.team2_name || 'TBA'}`}
      arrow
    >
      <div
        className="tike-game"
        style={{
          backgroundColor: STATUS_COLORS[game.status],
          color: textColor,
        }}
      >
        {game.display}
      </div>
    </Tooltip>
  );
}

function BlockBox({ block, showGames }: { block: OverviewBlock; showGames: boolean }) {
  const bgColor = LEAGUE_COLORS[block.league_id] || '#f0f0f0';
  
  if (showGames && block.games) {
    return (
      <div className="tike-block" style={{ backgroundColor: bgColor }}>
        {block.games.map((game) => (
          <GameBox key={game.game_id} game={game} />
        ))}
      </div>
    );
  }
  
  // Block summary view
  const total = block.total || 0;
  const finished = (block.finished || 0) + (block.late || 0);
  const progress = total > 0 ? (finished / total) * 100 : 0;
  const hasLate = (block.late || 0) > 0;
  
  return (
    <Tooltip
      title={`${block.paper_id}: ${finished}/${total} complete${hasLate ? ` (${block.late} late)` : ''}`}
      arrow
    >
      <div 
        className="tike-block tike-block-summary" 
        style={{ 
          backgroundColor: bgColor,
          borderColor: hasLate ? STATUS_COLORS.late : 'transparent',
        }}
      >
        <span className="tike-block-id">{block.paper_id}</span>
        <LinearProgress 
          variant="determinate" 
          value={progress}
          sx={{ 
            width: '100%', 
            height: 4,
            backgroundColor: 'rgba(0,0,0,0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: hasLate ? STATUS_COLORS.late : STATUS_COLORS.finished,
            },
          }}
        />
      </div>
    </Tooltip>
  );
}

function CourtRow({ court, showGames }: { court: OverviewCourt; showGames: boolean }) {
  return (
    <div className="tike-court-row">
      <Link
        href={`/admin/courts/${court.court_id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="tike-court-id"
        underline="hover"
      >
        {court.court_id}
      </Link>
      <div className="tike-blocks">
        {court.blocks.map((block) => (
          <Link
            key={block.paper_id}
            href={`/admin/blocks/${block.paper_id}`}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
          >
            <BlockBox block={block} showGames={showGames} />
          </Link>
        ))}
      </div>
    </div>
  );
}

function RoundsView({ rounds }: { rounds: OverviewRound[] }) {
  // Group by league
  const byLeague = useMemo(() => {
    const groups: Record<string, OverviewRound[]> = {};
    for (const r of rounds) {
      if (!groups[r.league_id]) groups[r.league_id] = [];
      groups[r.league_id].push(r);
    }
    return groups;
  }, [rounds]);
  
  const leagueOrder = ['yle', 'pro', 'frm'];
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {leagueOrder.filter(l => byLeague[l]).map((league) => (
        <Paper key={league} sx={{ p: 2, backgroundColor: LEAGUE_COLORS[league] }}>
          <Typography variant="h6" sx={{ mb: 2, textTransform: 'uppercase' }}>
            {league === 'yle' ? 'Yleinen' : league === 'pro' ? 'PRO' : 'Firma'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {byLeague[league].sort((a, b) => a.round - b.round).map((r) => {
              const finished = r.finished + r.late;
              const progress = r.total > 0 ? (finished / r.total) * 100 : 0;
              const hasLate = r.late > 0;
              
              return (
                <Paper 
                  key={r.round} 
                  sx={{ 
                    p: 2, 
                    minWidth: 150,
                    border: hasLate ? `2px solid ${STATUS_COLORS.late}` : undefined,
                  }}
                >
                  <Typography variant="subtitle2">Round {r.round}</Typography>
                  <Typography variant="h5">{finished}/{r.total}</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress}
                    sx={{ 
                      mt: 1,
                      height: 8,
                      borderRadius: 1,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: hasLate ? STATUS_COLORS.late : STATUS_COLORS.finished,
                      },
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {r.scheduled > 0 && (
                      <Chip size="small" label={`${r.scheduled} waiting`} />
                    )}
                    {r.started > 0 && (
                      <Chip 
                        size="small" 
                        label={`${r.started} playing`} 
                        sx={{ backgroundColor: STATUS_COLORS.started, color: '#fff' }}
                      />
                    )}
                    {r.late > 0 && (
                      <Chip 
                        size="small" 
                        label={`${r.late} late`} 
                        sx={{ backgroundColor: STATUS_COLORS.late, color: '#fff' }}
                      />
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

function Legend() {
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 16, height: 16, backgroundColor: STATUS_COLORS.scheduled, border: '1px solid #ccc' }} />
        <Typography variant="caption">Scheduled</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 16, height: 16, backgroundColor: STATUS_COLORS.started }} />
        <Typography variant="caption">Started</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 16, height: 16, backgroundColor: STATUS_COLORS.finished }} />
        <Typography variant="caption">Finished</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 16, height: 16, backgroundColor: STATUS_COLORS.late }} />
        <Typography variant="caption">Late</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 16, height: 16, backgroundColor: STATUS_COLORS.cancelled }} />
        <Typography variant="caption">Cancelled</Typography>
      </Box>
      <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 16, height: 16, backgroundColor: LEAGUE_COLORS.yle }} />
        <Typography variant="caption">YLE</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 16, height: 16, backgroundColor: LEAGUE_COLORS.pro }} />
        <Typography variant="caption">PRO</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 16, height: 16, backgroundColor: LEAGUE_COLORS.frm }} />
        <Typography variant="caption">FRM</Typography>
      </Box>
    </Box>
  );
}

export function OverviewPage() {
  const [grouping, setGrouping] = useState<'games' | 'blocks' | 'rounds'>('blocks');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval] = useState(30000); // 30 seconds

  const { data, isLoading, error, dataUpdatedAt } = useQuery<TournamentOverviewResponse>({
    queryKey: ['tournament-overview', grouping],
    queryFn: () => adminApi.getOverview(grouping),
    refetchInterval: autoRefresh ? refreshInterval : false,
  });

  const lastUpdate = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '-';

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Error loading overview: {(error as Error).message}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" fontWeight={700}>
          Tournament Overview (TiKe)
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Last update: {lastUpdate}
          </Typography>
          <FormControlLabel
            control={
              <Switch 
                checked={autoRefresh} 
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
              />
            }
            label="Auto-refresh"
          />
          <ToggleButtonGroup
            value={grouping}
            exclusive
            onChange={(_, value) => value && setGrouping(value)}
            size="small"
          >
            <ToggleButton value="games">Games</ToggleButton>
            <ToggleButton value="blocks">Blocks</ToggleButton>
            <ToggleButton value="rounds">Rounds</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Legend />

      {data?.group === 'rounds' && 'rounds' in data ? (
        <RoundsView rounds={data.rounds} />
      ) : data && 'courts' in data ? (
        <div className="tike-container">
          {data.courts.map((court) => (
            <CourtRow 
              key={court.court_id} 
              court={court} 
              showGames={grouping === 'games'} 
            />
          ))}
        </div>
      ) : null}
    </Box>
  );
}

